import { BOARD_SIZE, pieceVisualRenderSizePx } from "../../pages/puzzles/jigsaw/PieceVisual";

/** Shared chrome/board metrics for the fixed-frame jigsaw capture pages. */
export const PAD = 12;
/** Promo capture chrome — large enough to read on video, not real-play compact UI. */
export const TOOLBAR_H = 96;
/** @deprecated Landscape capture now uses a top toolbar; kept for any leftover callers. */
export const TOOLBAR_COL_W = 108;
export const TOOLBAR_GAP = 10;
/** Gap between the board and the tray beside (landscape) or under it (portrait). */
export const TRAY_GAP = 16;
export const TRAY_EDGE_PAD = 6;
/** Gap between left reference preview and the board (landscape). */
export const PREVIEW_GAP = 16;
/** Tray columns on landscape frames; rows on portrait frames. */
export const TRAY_COLUMN_COUNT = 1;
export const TRAY_ROW_COUNT = 2;
export const TRAY_CELL_GAP = 8;
/** Tray piece size vs board cells — smaller than the board so the board leads the shot. */
export const TRAY_PIECE_SCALE = 0.8;

/** Large promo-capture toolbar targets (not the compact real-play bar). */
export const TOOLBAR_ICON_SX = {
    minWidth: 72,
    minHeight: 72,
    width: 72,
    height: 72,
    padding: "14px",
    boxSizing: "border-box",
    color: "#3BA666",
    "& .MuiSvgIcon-root": { fontSize: "2.35rem" },
};

/** Landscape frames have spare width for a tray column; portrait frames stack it under. */
export function isTrayBesideBoard(frameW, frameH) {
    return frameW > frameH;
}

/** Tray thickness: column width when beside the board, strip height when under it. */
export function trayBlockPx(circlePx, beside) {
    const count = beside ? TRAY_COLUMN_COUNT : TRAY_ROW_COUNT;
    return circlePx * count + TRAY_CELL_GAP * Math.max(0, count - 1) + TRAY_EDGE_PAD;
}

/**
 * Board side for promo capture.
 * Landscape (1080p): top toolbar, left preview, board, right tray.
 * Portrait: top toolbar, board, tray under the board.
 */
export function computeBoardSidePx(gridN, frameW, frameH) {
    const beside = isTrayBesideBoard(frameW, frameH);
    const availH = frameH - TOOLBAR_H - TOOLBAR_GAP - PAD * 2;
    const availW = frameW - PAD * 2;
    if (!(gridN > 0)) return Math.floor(Math.min(availH, availW));

    const cellPx = BOARD_SIZE / gridN;
    const pieceAtScale1 = pieceVisualRenderSizePx(cellPx);
    let boardSide = Math.floor(Math.min(availH, availW));
    for (let i = 0; i < 3; i += 1) {
        const scale = boardSide / BOARD_SIZE;
        const circle = Math.round(pieceAtScale1 * scale * TRAY_PIECE_SCALE) + 2;
        const trayBlock = trayBlockPx(circle, beside) + TRAY_GAP;
        if (beside) {
            // Leave room for a half-size square reference preview on the left.
            const fullPreview = Math.max(
                140,
                Math.min(Math.floor(availW * 0.2), boardSide, availH),
            );
            const previewReserve = Math.max(72, Math.floor(fullPreview / 2));
            boardSide = Math.floor(
                Math.min(availH, Math.max(0, availW - trayBlock - PREVIEW_GAP - previewReserve)),
            );
        } else {
            boardSide = Math.floor(Math.min(availW, Math.max(0, availH - trayBlock)));
        }
    }
    return Math.max(20, boardSide);
}

/** Square preview size — half of the leftover width beside board + tray. */
export function computePreviewSidePx(boardSide, trayThickness, frameW) {
    const availW = frameW - PAD * 2;
    const leftover = availW - boardSide - trayThickness - TRAY_GAP - PREVIEW_GAP;
    if (!(leftover > 0) || !(boardSide > 0)) return 0;
    const full = Math.min(boardSide, leftover);
    return Math.max(0, Math.floor(full / 2));
}
